// Embaralhamento REPRODUTÍVEL: a mesma semente devolve sempre a mesma ordem.
//
// Existe porque o sorteio dos cards comuns não pode depender de onde o aluno
// abriu a trilha. Com `Math.random()` ele era feito uma vez e guardado no
// navegador — o que dava dois problemas:
//
// 1. o mesmo aluno, na mesma trilha, recebia cards DIFERENTES em casa e na
//    escola, porque cada navegador sorteava o seu;
// 2. se o `localStorage` fosse limpo no meio da trilha, o sorteio era refeito e
//    o álbum do encerramento (derivado dele) passava a mostrar cards que o aluno
//    nunca abriu nas cerimônias.
//
// Derivando de (aluno, trilha), os dois somem sem precisar de servidor: o
// resultado é o mesmo em qualquer lugar, sempre.
//
// Nada aqui conhece o AVA — recebe partes de semente já prontas. Quem sabe que
// elas são `userId` e `allocationId` é `ava/progressoDoAluno.js`.

// FNV-1a de 32 bits. Escolhido por ser curto, sem dependência e estável entre
// motores: o valor precisa ser o mesmo em todo navegador e no Node dos testes,
// hoje e daqui a dois anos. `Math.imul` é o que mantém a multiplicação em 32
// bits — sem ele o número estoura para ponto flutuante e o hash muda de máquina
// para máquina.
export function semente(...partes) {
  const texto = partes.join(":");
  let hash = 2166136261;
  for (let i = 0; i < texto.length; i += 1) {
    hash ^= texto.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

// mulberry32: gerador pseudoaleatório de 32 bits, ~10 linhas e boa distribuição.
// Não precisa ser criptográfico — precisa ser determinístico e espalhar bem.
function gerador(estadoInicial) {
  let estado = estadoInicial >>> 0;
  return function proximo() {
    estado = (estado + 0x6d2b79f5) >>> 0;
    let t = Math.imul(estado ^ (estado >>> 15), 1 | estado);
    // `t ^= X` e `t = X ^ t` dão o mesmo valor (XOR é comutativo, e X é avaliado
    // com o `t` anterior nos dois casos). Escrito assim por causa do
    // operator-assignment do lint — a forma original é a do mulberry32.
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Devolve uma função de embaralhar (mesma assinatura da que `sortearComuns`
 * espera) presa às partes de semente dadas.
 *
 * @param {...(string|number)} partes  o que identifica este sorteio
 * @returns {(lista: Array) => Array}
 */
export function embaralharComSemente(...partes) {
  return function embaralhar(lista) {
    const proximo = gerador(semente(...partes));
    const copia = [...(lista || [])];
    // Fisher-Yates, o mesmo do embaralhamento aleatório que isto substitui.
    for (let i = copia.length - 1; i > 0; i -= 1) {
      const j = Math.floor(proximo() * (i + 1));
      [copia[i], copia[j]] = [copia[j], copia[i]];
    }
    return copia;
  };
}

/**
 * Devolve o GERADOR cru — números em [0, 1) —, preso às partes de semente dadas.
 *
 * Irmão de `embaralharComSemente`, e existe porque não todo sorteio é uma ordem:
 * o das cartas RARAS é uma moeda por fase rara (ver `CHANCE_DE_CARTA_RARA` em
 * ./recompensaDaFase.js), e embaralhar não responde "caiu ou não caiu".
 *
 * Cada chamada devolve um gerador NOVO, do estado inicial: dois sorteios com as
 * mesmas partes dão o mesmo resultado, que é a propriedade inteira deste arquivo.
 * Consequência prática: quem chama não guarda o gerador entre renderizações — e
 * não deve, porque o gerador tem estado e reusá-lo faria o resultado depender de
 * quantas vezes o componente renderizou.
 *
 * @param {...(string|number)} partes  o que identifica este sorteio
 * @returns {() => number}
 */
export function aleatorioComSemente(...partes) {
  return gerador(semente(...partes));
}
